import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import "./CreateUserAdminForm.css";
import axios from "axios";

const CreateUserAdminForm = ({ onClose, initialData, additionalButtons }) => {
  const [formData, setFormData] = useState({
    email: "",
    firstName: "",
    lastName: "",
    statut: "assistante",
    ville: initialData?.ville || "",
    siret: initialData?.siret || "",
    siren: initialData?.siren || "",
    telephone: initialData?.telephone || "",
    service_principal: null,
    groupe_id: initialData?.groupe_id || 0,
    concessions_id: initialData?.concessions_id || 0,
  });

  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [groups, setGroups] = useState([]);
  const [concessions, setConcessions] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createdUser, setCreatedUser] = useState(null);
  const [copySuccess, setCopySuccess] = useState({
    identifier: false,
    password: false
  });
  const [filteredConcessions, setFilteredConcessions] = useState([]);
  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5002/api";

  useEffect(() => {
    document.body.classList.add('modal-open');

    return () => {
      document.body.classList.remove('modal-open');
    };
  }, []);

  useEffect(() => {
    const fetchGroupsAndConcessions = async () => {
      try {
        const [groupsResponse, concessionsResponse] = await Promise.all([
          axios.get(`${API_URL}/groups`, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }),
          axios.get(`${API_URL}/concessions`, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }),
        ]);

        console.log(
          "🔍 Récupération des groupes et concessions:",
          groupsResponse,
          concessionsResponse
        );
        setGroups(groupsResponse.data);
        setConcessions(concessionsResponse.data);
      } catch (error) {
        console.error(
          "Erreur lors de la récupération des groupes et concessions:",
          error
        );
        setError("Erreur lors de la récupération des groupes et concessions");
      }
    };

    fetchGroupsAndConcessions();
  }, [API_URL]);

  useEffect(() => {
    if (formData.groupe_id) {
      const filtered = concessions.filter(
        (c) => c.groupe_id === parseInt(formData.groupe_id)
      );
      setFilteredConcessions(filtered);
  
      // Réinitialise la concession si elle ne correspond plus
      if (!filtered.some(c => c.id === parseInt(formData.concessions_id))) {
        setFormData(prev => ({ ...prev, concessions_id: "" }));
      }
    } else {
      setFilteredConcessions(concessions);
    }
  }, [formData.groupe_id, concessions]);

  useEffect(() => {
    const selectedConcession = concessions.find(
      (c) => c.id === parseInt(formData.concessions_id)
    );
  
    if (selectedConcession) {
      const siret = selectedConcession.siret || "";
      const siren = siret.substring(0, 9);
  
      setFormData(prev => ({
        ...prev,
        groupe_id: selectedConcession.groupe_id,
        siret,
        siren
      }));
    }
  }, [formData.concessions_id, concessions]);
  
  

  const validateForm = () => {
    const errors = {};
    let isValid = true;
    
    // Vérification de l'email
    if (!formData.email) {
      errors.email = "L'email est requis";
      isValid = false;
    } else if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(formData.email)) {
      errors.email = "L'email est invalide";
      isValid = false;
    }
    
    // Vérification du prénom
    if (!formData.firstName || formData.firstName.trim() === "") {
      errors.firstName = "Le prénom est requis";
      isValid = false;
    }
    
    // Vérification du nom
    if (!formData.lastName || formData.lastName.trim() === "") {
      errors.lastName = "Le nom est requis";
      isValid = false;
    }
    
    // Vérification de la ville
    if (!formData.ville || formData.ville.trim() === "") {
      errors.ville = "La ville est requise";
      isValid = false;
    }
    
    // Vérification du SIRET
    if (!formData.siret) {
      errors.siret = "Le SIRET est requis";
      isValid = false;
    } else if (formData.siret.length !== 14) {
      errors.siret = "Le SIRET doit contenir 14 chiffres";
      isValid = false;
    }
    
    // Vérification du SIREN
    if (!formData.siren) {
      errors.siren = "Le SIREN est requis";
      isValid = false;
    } else if (formData.siren.length !== 9) {
      errors.siren = "Le SIREN doit contenir 9 chiffres";
      isValid = false;
    }
    
    // Vérification du téléphone
    if (!formData.telephone) {
      errors.telephone = "Le téléphone est requis";
      isValid = false;
    } else if (formData.telephone.length !== 10) {
      errors.telephone = "Le téléphone doit contenir 10 chiffres";
      isValid = false;
    }
    
    // Vérification de la concession
    if (!formData.concessions_id) {
      errors.concessions_id = "La concession est requise";
      isValid = false;
    }
    
    setFieldErrors(errors);
    
    if (!isValid) {
      setError("Veuillez corriger les erreurs dans le formulaire");
    }
    
    return isValid;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setError(null);
    
    // Effacer les erreurs spécifiques au champ lorsqu'il est modifié
    if (fieldErrors[name]) {
      setFieldErrors({
        ...fieldErrors,
        [name]: null
      });
    }

    // Validation spécifique pour les champs numériques
    if (name === 'telephone' || name === 'siret' || name === 'siren') {
      // Vérifier que la valeur ne contient que des chiffres
      if (value !== '' && !/^\d+$/.test(value)) {
        setFieldErrors({
          ...fieldErrors,
          [name]: `Le champ ${name} ne doit contenir que des chiffres`
        });
        return;
      }
      
      // Limiter la longueur et valider le format
      if (name === 'telephone') {
        if (value.length > 10) {
          return;
        }
        if (value.length > 0 && value.length < 10) {
          setFieldErrors({
            ...fieldErrors,
            [name]: "Le numéro de téléphone doit contenir 10 chiffres"
          });
        } else {
          setFieldErrors({
            ...fieldErrors,
            [name]: null
          });
        }
      }
      
      if (name === 'siret') {
        if (value.length > 14) {
          return;
        }
        if (value.length > 0 && value.length < 14) {
          setFieldErrors({
            ...fieldErrors,
            [name]: "Le SIRET doit contenir 14 chiffres"
          });
        } else {
          setFieldErrors({
            ...fieldErrors,
            [name]: null
          });
        }
      }
      
      if (name === 'siren') {
        if (value.length > 9) {
          return;
        }
        if (value.length > 0 && value.length < 9) {
          setFieldErrors({
            ...fieldErrors,
            [name]: "Le SIREN doit contenir 9 chiffres"
          });
        } else {
          setFieldErrors({
            ...fieldErrors,
            [name]: null
          });
        }
      }
    }
    
    // Validation de l'email
    if (name === 'email') {
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (value && !emailRegex.test(value)) {
        setFieldErrors({
          ...fieldErrors,
          [name]: "Format d'email invalide"
        });
      } else {
        setFieldErrors({
          ...fieldErrors,
          [name]: null
        });
      }
    }
    
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCopyToClipboard = (text, type) => {
    if (text) {
      navigator.clipboard.writeText(text);
      setCopySuccess({
        ...copySuccess,
        [type]: true
      });
      
      // Reset copy success after 3 seconds
      setTimeout(() => {
        setCopySuccess(prev => ({
          ...prev,
          [type]: false
        }));
      }, 3000);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const response = await axios.post(
        `${API_URL}/users`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data) {
        setCreatedUser(response.data.user);
        setError(null);
      }
    } catch (error) {
      console.error("Erreur lors de la création de l'utilisateur:", error);
      
      // Traitement des erreurs d'unicité
      if (error.response?.data) {
        const errorData = error.response.data;
        
        if (errorData.errorType === 'DUPLICATE_EMAIL') {
          setFieldErrors({
            ...fieldErrors,
            email: "Cet email est déjà utilisé par un autre utilisateur"
          });
          setError("Cet email est déjà utilisé");
        } else if (errorData.errorType === 'DUPLICATE_TELEPHONE') {
          setFieldErrors({
            ...fieldErrors,
            telephone: "Ce numéro de téléphone est déjà utilisé par un autre utilisateur"
          });
          setError("Ce numéro de téléphone est déjà utilisé");
        } else if (errorData.field) {
          // Si l'API renvoie le champ spécifique qui pose problème
          setFieldErrors({
            ...fieldErrors,
            [errorData.field]: errorData.message || "Champ invalide"
          });
          setError(errorData.message || "Veuillez corriger le formulaire");
        } else {
          setError(errorData.message || "Erreur lors de la création de l'utilisateur");
        }
      } else {
        setError("Erreur lors de la création de l'utilisateur");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target.className === 'modal-overlay') {
      onClose();
    }
  };

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="modal-content">
        <div className="modal-header">
          <h2 className="form-title">Créer un utilisateur</h2>
          <button className="close-button" onClick={onClose}>
            ✕
          </button>
        </div>

        {error && <div className="error-message">{error}</div>}

        {createdUser ? (
          <div className="modal-body">
            <div className="success-message">
              <p>Informations de connexion à communiquer:</p>
            </div>
            
            <div className="credentials-container">
              <div className="credential-group">
                <label>Identifiant:</label>
                <div className="credential-value">
                  <input 
                    type="text" 
                    value={createdUser.identifier || ""} 
                    readOnly 
                    className="credential-input"
                    style={{ 
                      fontFamily: 'monospace', 
                      fontSize: '1rem',
                      textTransform: 'none', // Prevent any text transformation
                      letterSpacing: '0.5px' // Improve readability
                    }}
                  />
                  <button 
                    className="copy-button"
                    onClick={() => handleCopyToClipboard(createdUser.identifier, 'identifier')}
                  >
                    {copySuccess.identifier ? "Copié ✓" : "Copier"}
                  </button>
                </div>
              </div>
              
              <div className="credential-group">
                <label>Mot de passe temporaire:</label>
                <div className="credential-value">
                  <input 
                    type="text" 
                    value={createdUser.tempPassword || ""} 
                    readOnly 
                    className="credential-input"
                    style={{ 
                      fontFamily: 'monospace', 
                      fontSize: '1rem',
                      textTransform: 'none', // Prevent any text transformation
                      letterSpacing: '0.5px' // Improve readability
                    }}
                  />
                  <button 
                    className="copy-button"
                    onClick={() => handleCopyToClipboard(createdUser.tempPassword, 'password')}
                  >
                    {copySuccess.password ? "Copié ✓" : "Copier"}
                  </button>
                </div>
              </div>
              
              <div className="credential-notice">
                ⚠️ Attention: L&apos;identifiant et le mot de passe sont sensibles à la casse.
                Veuillez les copier exactement comme affichés.
              </div>
            </div>
          </div>
        ) : (
          <div className="modal-body">
            <div className="form-section">
              <div className="form-group">
                <label className="required-field">Statut</label>
                <select
                  name="statut"
                  value={formData.statut}
                  onChange={handleChange}
                  required
                  className={fieldErrors.statut ? "input-error" : ""}
                >
                  <option value="assistante">Assistante</option>
                  <option value="commercial">Commercial</option>
                </select>
                {fieldErrors.statut && <div className="field-error">{fieldErrors.statut}</div>}
              </div>

              <div className="form-group">
                <label className="required-field">Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  autoFocus
                  className={fieldErrors.email ? "input-error" : ""}
                />
                {fieldErrors.email && <div className="field-error">{fieldErrors.email}</div>}
              </div>

              <div className="form-group">
                <label className="required-field">Telephone</label>
                <input
                  type="text"
                  name="telephone"
                  value={formData.telephone}
                  onChange={handleChange}
                  required
                  maxLength={10}
                  className={fieldErrors.telephone ? "input-error" : ""}
                />
                {fieldErrors.telephone && <div className="field-error">{fieldErrors.telephone}</div>}
              </div>
            </div>

            <div className="form-section">
              <div className="form-group-row">
                <div className="form-group">
                  <label className="required-field">Prénom</label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    className={fieldErrors.firstName ? "input-error" : ""}
                  />
                  {fieldErrors.firstName && <div className="field-error">{fieldErrors.firstName}</div>}
                </div>

                <div className="form-group">
                  <label className="required-field">Nom</label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                    className={fieldErrors.lastName ? "input-error" : ""}
                  />
                  {fieldErrors.lastName && <div className="field-error">{fieldErrors.lastName}</div>}
                </div>
              </div>

              <div className="form-group">
                <label className="required-field">Ville</label>
                <input
                  type="text"
                  name="ville"
                  value={formData.ville}
                  onChange={handleChange}
                  required
                  className={fieldErrors.ville ? "input-error" : ""}
                />
                {fieldErrors.ville && <div className="field-error">{fieldErrors.ville}</div>}
              </div>

              <div className="form-group-row">
                <div className="form-group">
                  <label className="required-field">SIRET (14 chiffres)</label>
                  <input
                    type="text"
                    name="siret"
                    value={formData.siret}
                    onChange={handleChange}
                    required
                    maxLength={14}
                    className={fieldErrors.siret ? "input-error" : ""}
                  />
                  {fieldErrors.siret && <div className="field-error">{fieldErrors.siret}</div>}
                </div>

                <div className="form-group">
                  <label className="required-field">SIREN (9 chiffres)</label>
                  <input
                    type="text"
                    name="siren"
                    value={formData.siren}
                    onChange={handleChange}
                    required
                    maxLength={9}
                    className={fieldErrors.siren ? "input-error" : ""}
                  />
                  {fieldErrors.siren && <div className="field-error">{fieldErrors.siren}</div>}
                </div>
              </div>
            </div>

            <div className="form-section">
              <div className="form-group">
                <label>Groupe</label>
                <select
                  name="groupe_id"
                  value={formData.groupe_id}
                  onChange={handleChange}
                  className={fieldErrors.groupe_id ? "input-error" : ""}
                >
                  <option value="">Sélectionner un groupe</option>
                  {groups.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.nom}
                    </option>
                  ))}
                </select>
                {fieldErrors.groupe_id && <div className="field-error">{fieldErrors.groupe_id}</div>}
              </div>

              <div className="form-group">
                <label className="required-field">Concession</label>
                <select
                  name="concessions_id"
                  value={formData.concessions_id}
                  onChange={handleChange}
                  required
                  className={fieldErrors.concessions_id ? "input-error" : ""}
                >
                  <option value="">Sélectionner une concession</option>
                  {filteredConcessions.map((concession) => (
                    <option key={concession.id} value={concession.id}>
                      {concession.nom}
                    </option>
                  ))}
                </select>
                {fieldErrors.concessions_id && <div className="field-error">{fieldErrors.concessions_id}</div>}
              </div>

              <div className="form-group">
                <label>Service Principal</label>
                <select
                  name="service_principal"
                  value={formData.service_principal}
                  onChange={handleChange}
                  className={fieldErrors.service_principal ? "input-error" : ""}
                >
                  <option value="">Sélectionner un service</option>
                  <option value="VO">{"Véhicules d'occasion"}</option>
                  <option value="VN">{"Véhicules neufs"}</option>
                  <option value="AV">{"Autres véhicules"}</option>
                </select>
                {fieldErrors.service_principal && <div className="field-error">{fieldErrors.service_principal}</div>}
              </div>
            </div>
          </div>
        )}

        <div className="modal-footer">
          {createdUser ? (
            <button
              type="button"
              className="button primary"
              onClick={onClose}
            >
              Fermer
            </button>
          ) : (
            <>
              {additionalButtons}
              <button type="button" className="button secondary" onClick={onClose}>
                Annuler
              </button>
              <button
                type="button"
                className="button primary"
                onClick={handleSubmit}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Création en cours..." : "Créer"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

CreateUserAdminForm.propTypes = {
  onClose: PropTypes.func.isRequired,
  initialData: PropTypes.object,
  additionalButtons: PropTypes.node,
};

export default CreateUserAdminForm;
